import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSupabaseAuth } from '../context/AuthContext';
import { useDemoPersona } from '../context/DemoPersonaContext';
import { fetchDemoPersonas, startDemoPersonaSession } from '../lib/demoProvisioning';
import Badge from './Badge';

export default function DemoLoginsPanel({ programme }) {
  const { getAccessTokenSilently } = useAuth0();
  const { profile } = useSupabaseAuth();
  const { session, enterDemoPersona, exitDemoPersona, isDemoView } = useDemoPersona();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!profile?.organisation_id || !programme?.is_demo) return;
    setLoading(true);
    try {
      const rows = await fetchDemoPersonas(profile.organisation_id);
      setPersonas(rows);
    } catch (e) {
      console.error('[DemoLoginsPanel]', e);
      setError(e.message || 'Failed to load demo personas');
    } finally {
      setLoading(false);
    }
  }, [profile?.organisation_id, programme?.is_demo]);

  useEffect(() => { load(); }, [load]);

  if (!programme?.is_demo || profile?.platform_role !== 'admin') return null;

  async function handleLogin(persona) {
    setError('');
    setBusyId(persona.id);
    try {
      const data = await startDemoPersonaSession(
        getAccessTokenSilently,
        persona.id,
        programme.id,
      );
      enterDemoPersona({
        persona: data.persona,
        programmeId: programme.id,
        access_role: data.access_role,
        role_label: data.role_label,
      });
    } catch (e) {
      setError(e.message || 'Could not start demo session');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="demo-logins-panel" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <strong>Demo logins</strong>
        <Badge cls="am">Preview</Badge>
      </div>
      <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
        Explore this demo programme as a pre-configured persona. Your Auth0 session stays as admin — this is an in-product preview.
      </p>
      {error && <div className="form-error" role="alert">{error}</div>}
      {loading ? (
        <div className="auth-info">Loading personas…</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {personas.map((p) => (
            <button
              key={p.id}
              type="button"
              className="btn-secondary"
              disabled={busyId === p.id}
              onClick={() => handleLogin(p)}
            >
              {busyId === p.id ? 'Starting…' : `Log in as ${p.full_name}`}
            </button>
          ))}
        </div>
      )}
      {isDemoView && session?.programmeId === programme.id && (
        <div style={{ marginTop: 12, padding: 12, background: '#f5f4ef', borderRadius: 8 }}>
          <div style={{ fontSize: 13 }}>
            Viewing as <strong>{session.persona?.full_name}</strong> ({session.roleLabel})
          </div>
          <button type="button" className="btn-link" style={{ marginTop: 6 }} onClick={exitDemoPersona}>
            Exit demo view
          </button>
        </div>
      )}
    </div>
  );
}
